import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Empty, Select, Spin } from 'antd';

import {
  searchSyntheticBondSourceAssets,
  SyntheticBondSourceAssetOption,
  syntheticBondSourceSearchKey,
} from 'state';
import { StoreState } from '../../../state/storeState';

type Props = {
  currencyId: number | null;
  disabled?: boolean;
  fallbackAsset?: SyntheticBondSourceAssetOption | null;
  initialLoader?: boolean;
  onChange: (assetId: number | null) => void;
  value: number | null;
};

const assetLabel = (asset: SyntheticBondSourceAssetOption) => {
  const identifier = asset.ric || asset.ticker;
  return `${asset.name}${identifier ? ` · ${identifier}` : ''}`;
};

const SourceAssetPicker: React.FC<Props> = ({
  currencyId,
  disabled,
  fallbackAsset,
  initialLoader = false,
  onChange,
  value,
}) => {
  const dispatch = useDispatch();
  const key = currencyId ? syntheticBondSourceSearchKey(currencyId) : '';
  const entry = useSelector((state: StoreState) =>
    key ? state.app.synthetic_bonds.sourceAssetSearchByKey[key] : undefined,
  );

  useEffect(() => {
    if (!currencyId || !initialLoader) return;
    if (!entry?.fetching && !entry?.fetched && !entry?.error) {
      dispatch(searchSyntheticBondSourceAssets({ currency_id: currencyId }));
    }
  }, [
    currencyId,
    dispatch,
    entry?.error,
    entry?.fetched,
    entry?.fetching,
    initialLoader,
  ]);

  const assets = useMemo(() => {
    const results = [...(entry?.data?.results || [])];
    if (
      fallbackAsset &&
      !results.some(asset => asset.id === fallbackAsset.id)
    ) {
      results.unshift(fallbackAsset);
    }
    return results;
  }, [entry?.data?.results, fallbackAsset]);

  return (
    <Select<number>
      allowClear
      className="synthetic-bonds__source-picker"
      disabled={disabled || !currencyId}
      loading={entry?.fetching}
      notFoundContent={
        entry?.fetching ? (
          <Spin size="small" />
        ) : (
          <div className="synthetic-bonds__source-empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                entry?.error ||
                (currencyId
                  ? 'No eligible same-currency GovtBond assets'
                  : 'Select a currency first')
              }
            />
            {entry?.error && currencyId && (
              <Button
                size="small"
                onClick={() =>
                  dispatch(
                    searchSyntheticBondSourceAssets({
                      currency_id: currencyId,
                    }),
                  )
                }
              >
                Retry
              </Button>
            )}
          </div>
        )
      }
      optionLabelProp="label"
      optionFilterProp="label"
      options={assets.map(asset => ({
        value: asset.id,
        label: assetLabel(asset),
        title: assetLabel(asset),
      }))}
      placeholder="Choose source UnderlyingAsset"
      showSearch
      value={value ?? undefined}
      onChange={next => onChange(next ?? null)}
    />
  );
};

export default SourceAssetPicker;
